import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../App';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Map, Plus, ChevronUp, Edit3, Trash2, Save } from 'lucide-react';
import RichTextEditor from '../components/RichTextEditor';
import { toast } from 'sonner';

const STATUSES = [
  { id: 'planned', label: 'Planned', color: 'bg-amber-100 text-amber-800 border-amber-200', dot: 'bg-amber-500' },
  { id: 'in_progress', label: 'In Progress', color: 'bg-blue-100 text-blue-800 border-blue-200', dot: 'bg-blue-500' },
  { id: 'shipped', label: 'Shipped', color: 'bg-emerald-100 text-emerald-800 border-emerald-200', dot: 'bg-emerald-500' },
  { id: 'declined', label: 'Declined', color: 'bg-zinc-100 text-zinc-700 border-zinc-200', dot: 'bg-zinc-400' },
];

const Roadmap = ({ user, setUser }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [filter, setFilter] = useState('all');

  useEffect(() => { fetchItems(); }, []);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/roadmap`);
      setItems(res.data.items || []);
    } catch (e) {
      toast.error('Failed to load roadmap');
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditing({ title: '', description: '', status: 'planned', category: '' });
    setShowDialog(true);
  };

  const openEdit = (item) => { setEditing({ ...item }); setShowDialog(true); };

  const handleSave = async () => {
    if (!editing.title || !editing.description) {
      toast.error('Title and description are required');
      return;
    }
    try {
      if (editing.id) {
        const { id, ...payload } = editing;
        await axios.patch(`${API_URL}/roadmap/${id}`, {
          title: payload.title, description: payload.description,
          status: payload.status, category: payload.category,
        });
        toast.success('Updated');
      } else {
        await axios.post(`${API_URL}/roadmap`, editing);
        toast.success('Created');
      }
      setShowDialog(false);
      fetchItems();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this roadmap item?')) return;
    try {
      await axios.delete(`${API_URL}/roadmap/${id}`);
      toast.success('Deleted');
      fetchItems();
    } catch (e) {
      toast.error('Delete failed');
    }
  };

  const filteredItems = filter === 'all' ? items : items.filter(i => i.status === filter);
  const groupedByStatus = STATUSES.map(s => ({
    ...s,
    items: items.filter(i => i.status === s.id),
  }));

  return (
    <Layout user={user} setUser={setUser}>
      <div className="p-8 max-w-7xl mx-auto" data-testid="roadmap-page">
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="h-10 w-10 rounded-xl flex items-center justify-center bg-violet-600">
                <Map className="h-5 w-5 text-white" />
              </div>
              <h1 className="text-4xl font-bold text-zinc-900" style={{ fontFamily: 'Manrope, sans-serif', letterSpacing: '-0.02em' }}>Product Roadmap</h1>
            </div>
            <p className="text-sm text-zinc-600">Share what you're building, gather upvotes, and keep users in the loop.</p>
          </div>
          <Button onClick={openCreate} className="bg-violet-600 hover:bg-violet-700" data-testid="new-roadmap-button">
            <Plus className="h-4 w-4 mr-2" />Add Item
          </Button>
        </div>

        {/* Filter */}
        <div className="flex gap-2 mb-6 border-b border-zinc-200">
          <button
            onClick={() => setFilter('all')}
            className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors ${filter === 'all' ? 'border-violet-600 text-violet-700' : 'border-transparent text-zinc-600 hover:text-zinc-900'}`}
          >All ({items.length})</button>
          {STATUSES.map(s => (
            <button
              key={s.id}
              onClick={() => setFilter(s.id)}
              className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${filter === s.id ? 'border-violet-600 text-violet-700' : 'border-transparent text-zinc-600 hover:text-zinc-900'}`}
            >
              <span className={`h-1.5 w-1.5 rounded-full ${s.dot}`} />
              {s.label} ({items.filter(i => i.status === s.id).length})
            </button>
          ))}
        </div>

        {loading ? <p className="text-zinc-500">Loading…</p> : (
          filter === 'all' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {groupedByStatus.map(col => (
                <div key={col.id} className="space-y-3">
                  <div className="flex items-center gap-2 px-2 py-1">
                    <span className={`h-2 w-2 rounded-full ${col.dot}`} />
                    <span className="font-semibold text-sm text-zinc-700">{col.label}</span>
                    <span className="text-xs text-zinc-400">{col.items.length}</span>
                  </div>
                  {col.items.map(item => <RoadmapCard key={item.id} item={item} statusOpt={col} onEdit={openEdit} onDelete={handleDelete} />)}
                  {col.items.length === 0 && (
                    <div className="text-xs text-zinc-400 italic px-2 py-4 text-center border-2 border-dashed border-zinc-100 rounded-xl">Nothing here yet</div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-3">
              {filteredItems.map(item => {
                const statusOpt = STATUSES.find(s => s.id === item.status);
                return <RoadmapCard key={item.id} item={item} statusOpt={statusOpt} onEdit={openEdit} onDelete={handleDelete} />;
              })}
            </div>
          )
        )}

        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{editing?.id ? 'Edit Roadmap Item' : 'New Roadmap Item'}</DialogTitle>
              <DialogDescription>Add a feature, improvement, or upcoming change to the public roadmap.</DialogDescription>
            </DialogHeader>
            {editing && (
              <div className="space-y-3">
                <div>
                  <Label className="text-sm font-medium">Title</Label>
                  <Input value={editing.title} onChange={e => setEditing({...editing, title: e.target.value})} className="mt-1.5" data-testid="roadmap-title-input" />
                </div>
                <div>
                  <Label className="text-sm font-medium">Description</Label>
                  <div className="mt-1.5" data-testid="roadmap-description-input">
                    <RichTextEditor
                      value={editing.description}
                      onChange={(html) => setEditing({ ...editing, description: html })}
                      placeholder="Describe this feature or improvement…"
                      minHeight={200}
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-medium">Status</Label>
                  <div className="grid grid-cols-4 gap-2 mt-1.5">
                    {STATUSES.map(s => (
                      <button
                        key={s.id}
                        onClick={() => setEditing({...editing, status: s.id})}
                        className={`px-3 py-2 rounded-lg border-2 text-sm flex items-center gap-2 justify-center ${editing.status === s.id ? 'border-violet-400 bg-violet-50' : 'border-zinc-200 hover:border-zinc-300'}`}
                      >
                        <span className={`h-2 w-2 rounded-full ${s.dot}`} />{s.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-medium">Category (optional)</Label>
                  <Input value={editing.category || ''} onChange={e => setEditing({...editing, category: e.target.value})} className="mt-1.5" placeholder="e.g. Mobile, API" />
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
                  <Button onClick={handleSave} className="bg-violet-600 hover:bg-violet-700" data-testid="roadmap-save-button">
                    <Save className="h-4 w-4 mr-2" />Save
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

const RoadmapCard = ({ item, statusOpt, onEdit, onDelete }) => (
  <div className="bg-white border border-zinc-200 rounded-xl p-4 hover:shadow-sm transition-shadow group" data-testid={`roadmap-item-${item.id}`}>
    <div className="flex items-start justify-between gap-2 mb-2">
      <h3 className="font-semibold text-sm text-zinc-900 leading-snug flex-1">{item.title}</h3>
      <div className="flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium bg-zinc-50 text-zinc-700 border border-zinc-200">
        <ChevronUp className="h-3 w-3" />{item.upvote_count || 0}
      </div>
    </div>
    {item.category && <div className="text-xs text-zinc-500 mb-2">{item.category}</div>}
    <div className="opacity-0 group-hover:opacity-100 transition-opacity flex gap-1 -ml-1">
      <button onClick={() => onEdit(item)} className="p-1 hover:bg-zinc-100 rounded text-zinc-600"><Edit3 className="h-3.5 w-3.5" /></button>
      <button onClick={() => onDelete(item.id)} className="p-1 hover:bg-red-50 rounded text-red-500"><Trash2 className="h-3.5 w-3.5" /></button>
    </div>
  </div>
);

export default Roadmap;
